class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = 'mtij:recently-viewed';
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.currentHandle = (this.dataset.currentHandle || '').trim();
    this.grid = this.querySelector('[data-grid]');
    // Opt-in, set only on the cart drawer instance. Without it this component
    // behaves exactly as before, so the product and cart page grids are untouched.
    this.isCarousel = this.dataset.carousel === 'true';
    this.nav = this.querySelector('[data-nav]');
    this.index = 0;

    try {
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR'
      });
    } catch (e) {
      this.formatter = { format: (v) => v.toFixed(2).replace('.', ',') + ' €' };
    }
    this.render();
  }

  getHandles() {
    try {
      const raw = window.localStorage.getItem(this.STORAGE_KEY) || '[]';
      const handles = JSON.parse(raw);
      if (!Array.isArray(handles)) return [];
      const filtered = this.currentHandle
        ? handles.filter((h) => h !== this.currentHandle)
        : handles;
      return filtered.slice(0, this.limit);
    } catch (e) {
      return [];
    }
  }

  async fetchProduct(handle) {
    try {
      const response = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  async render() {
    if (!this.grid) {
      this.remove();
      return;
    }
    const handles = this.getHandles();
    if (!handles.length) {
      this.remove();
      return;
    }
    const products = await Promise.all(handles.map((h) => this.fetchProduct(h)));
    const valid = products.filter(Boolean);
    if (!valid.length) {
      this.remove();
      return;
    }
    this.grid.innerHTML = valid.map((p, i) => this.renderCard(p, i)).join('');
    // A single product needs no navigation: show the card, skip the dots.
    if (this.isCarousel && valid.length > 1) this.initCarousel(valid.length);
    this.classList.remove('is-loading');
    this.classList.add('is-loaded');
    this.removeAttribute('hidden');
  }

  renderCard(product, index) {
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const imageUrl = image
      ? (image.includes('?') ? image + '&width=600' : image + '?width=600')
      : '';
    const showVendor = this.dataset.showVendor === 'true';
    const vendor = showVendor && product.vendor
      ? '<p class="recently-viewed-mtij__card-vendor">' + this.escape(product.vendor) + '</p>'
      : '';
    const soldOut = product.available === false
      ? '<span class="recently-viewed-mtij__card-soldout">' + this.escape(this.dataset.i18nSoldOut || 'Sold out') + '</span>'
      : '';
    const price = this.formatter.format((product.price || 0) / 100);
    const compareAt = product.compare_at_price && product.compare_at_price > product.price
      ? '<s>' + this.formatter.format(product.compare_at_price / 100) + '</s>'
      : '';
    const altText = (product.featured_image && product.featured_image.alt) || product.title;
    const slideClass = this.isCarousel && index === 0 ? ' is-active' : '';
    const slideAttr = this.isCarousel ? ' data-slide="' + index + '"' : '';
    const url = this.escape(product.url);
    const quickBuyLabel = this.escape(
      (this.dataset.i18nQuickBuy || 'Quick view — [title]').replace('[title]', product.title)
    );

    // In the cart drawer the card is a narrow horizontal row, where a square
    // overlay button would crowd an 80px thumbnail. There the trigger is an
    // underlined text link sitting under the price instead.
    const asText = this.dataset.triggerStyle === 'text';

    const squareTrigger = asText
      ? ''
      : '<button type="button" class="quick-buy-trigger" data-quick-buy data-product-url="' +
        url +
        '" aria-haspopup="dialog" aria-controls="QuickBuyDialog" aria-label="' +
        quickBuyLabel +
        '">' +
        (this.dataset.iconQuickBuy || '+') +
        '</button>';

    const textTrigger = asText
      ? '<button type="button" class="quick-buy-trigger quick-buy-trigger--text" data-quick-buy data-product-url="' +
        url +
        '" aria-haspopup="dialog" aria-controls="QuickBuyDialog" aria-label="' +
        quickBuyLabel +
        '">' +
        this.escape(this.dataset.i18nAdd || 'Add to cart') +
        '</button>'
      : '';

    // A <div>, not an <a>: the trigger is a <button> and may not be nested in a link.
    return [
      '<div class="recently-viewed-mtij__card' + slideClass + '" role="listitem"' + slideAttr + '>',
      '<div class="recently-viewed-mtij__card-media">',
      '<a class="recently-viewed-mtij__card-media-link" href="' + url + '" tabindex="-1" aria-hidden="true"></a>',
      soldOut,
      imageUrl
        ? '<img class="recently-viewed-mtij__card-image" src="' + this.escape(imageUrl) + '" alt="' + this.escape(altText) + '" loading="lazy">'
        : '',
      squareTrigger,
      '</div>',
      '<div class="recently-viewed-mtij__card-details">',
      '<a class="recently-viewed-mtij__card-link" href="' + url + '">',
      vendor,
      '<h3 class="recently-viewed-mtij__card-title">' + this.escape(product.title) + '</h3>',
      '<p class="recently-viewed-mtij__card-price">',
      compareAt,
      '<span>' + price + '</span>',
      '</p>',
      '</a>',
      textTrigger,
      '</div>',
      '</div>'
    ].join('');
  }

  initCarousel(count) {
    if (!this.nav) return;

    this.classList.add('is-carousel');
    this.slides = Array.from(this.grid.querySelectorAll('[data-slide]'));
    this.index = 0;
    this.total = count;

    const prevLabel = this.escape(this.dataset.i18nPrev || 'Previous product');
    const nextLabel = this.escape(this.dataset.i18nNext || 'Next product');
    const caret = this.dataset.iconCaret || '';
    const padded = (n) => String(n).padStart(2, '0');

    // type="button" is mandatory: this component sits inside the cart form, and a
    // default submit button would send the customer to checkout.
    this.nav.innerHTML =
      '<div class="recently-viewed-mtij__nav-inner">' +
      '<button type="button" class="recently-viewed-mtij__arrow recently-viewed-mtij__arrow--prev" data-prev aria-label="' +
      prevLabel +
      '"><span class="svg-wrapper">' + caret + '</span></button>' +
      '<p class="recently-viewed-mtij__counter">' +
      '<span class="visually-hidden" data-counter-label aria-live="polite"></span>' +
      '<span aria-hidden="true"><span data-counter-current>01</span>/' + padded(count) + '</span>' +
      '</p>' +
      '<button type="button" class="recently-viewed-mtij__arrow recently-viewed-mtij__arrow--next" data-next aria-label="' +
      nextLabel +
      '"><span class="svg-wrapper">' + caret + '</span></button>' +
      '</div>';

    this.nav.removeAttribute('hidden');
    this.counter = this.nav.querySelector('[data-counter-current]');
    this.counterLabel = this.nav.querySelector('[data-counter-label]');
    this.updateCounter();

    const prev = this.nav.querySelector('[data-prev]');
    const next = this.nav.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

    this.nav.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
    });
  }

  updateCounter() {
    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    if (this.counterLabel) {
      const template = this.dataset.i18nCounter || 'Product [index] of [total]';
      this.counterLabel.textContent = template
        .replace('[index]', String(this.index + 1))
        .replace('[total]', String(this.total));
    }
  }

  goTo(next) {
    if (!this.slides || !this.slides.length) return;

    const total = this.slides.length;
    // Safe modulo so the chevrons wrap at both ends.
    const target = ((next % total) + total) % total;
    this.index = target;

    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === target));
    this.updateCounter();
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewed);
}
